import React, { useState, useEffect } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Offer } from "@/types/offer";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SortableOption } from "@/types/fieldsOptions";
import { isAvailabilityStatus, isSignupStatus } from "@/functions-shared/offer/offerStatusEnums";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { RegistrationEventsSection } from "@/components/admin/locations/components/calendar/dialogs/components/form-sections/RegistrationEventsSection";
import type { RegistrationEventFormValues } from "@/components/admin/locations/components/calendar/schemas/offerFormSchema";
import { Form } from "@/components/ui/form";

export interface BulkEditFormData {
  sessionTitle: {
    enabled: boolean;
    value: string;
  };
  sessionUrl: {
    enabled: boolean;
    value: string;
  };
  registrationUrl: {
    enabled: boolean;
    value: string;
  };
  signupStatus: {
    enabled: boolean;
    value: string;
  };
  availabilityStatus: {
    enabled: boolean;
    value: string;
  };
  sessionDescription: {
    enabled: boolean;
    value: string;
  };
  regularPrice: {
    enabled: boolean;
    value: string; // Will be converted to number when saving
  };
  priceNote: {
    enabled: boolean;
    value: string;
  };
  timingNote: {
    enabled: boolean;
    value: string;
  };
  currentSalePrice: {
    enabled: boolean;
    value: string; // Will be converted to number when saving
  };
  visibility: {
    enabled: boolean;
    value: string;
  };
  regEvents: {
    enabled: boolean;
    value: RegistrationEventFormValues[];
  };
}

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: BulkEditFormData) => void;
  selectedOffers: Offer[];
  signupStatusOptions?: SortableOption[];
  availabilityStatusOptions?: SortableOption[];
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedOffers,
  signupStatusOptions = [],
  availabilityStatusOptions = []
}) => {
  const selectedCount = selectedOffers.length;
  
  // Track if form has been submitted
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  const [formData, setFormData] = useState<BulkEditFormData>({
    sessionTitle: {
      enabled: false,
      value: ""
    },
    sessionUrl: {
      enabled: false,
      value: ""
    },
    registrationUrl: {
      enabled: false,
      value: ""
    },
    signupStatus: {
      enabled: false,
      value: "su_unknown"
    },
    availabilityStatus: {
      enabled: false,
      value: "av_unknown"
    },
    sessionDescription: {
      enabled: false,
      value: ""
    },
    regularPrice: {
      enabled: false,
      value: ""
    },
    priceNote: {
      enabled: false,
      value: ""
    },
    timingNote: {
      enabled: false,
      value: ""
    },
    currentSalePrice: {
      enabled: false,
      value: ""
    },
    visibility: {
      enabled: false,
      value: "published"
    },
    regEvents: {
      enabled: false,
      value: []
    }
  });

  const regEventsForm = useForm<{ regEvents: RegistrationEventFormValues[] }>({
    defaultValues: { regEvents: [] }
  });

  // Reset form when modal is closed after submission
  useEffect(() => {
    if (!isOpen && hasSubmitted) {
      setFormData({
        sessionTitle: { enabled: false, value: "" },
        sessionUrl: { enabled: false, value: "" },
        registrationUrl: { enabled: false, value: "" },
        signupStatus: { enabled: false, value: "su_unknown" },
        availabilityStatus: { enabled: false, value: "av_unknown" },
        sessionDescription: { enabled: false, value: "" },
        regularPrice: { enabled: false, value: "" },
        priceNote: { enabled: false, value: "" },
        timingNote: { enabled: false, value: "" },
        currentSalePrice: { enabled: false, value: "" },
        visibility: { enabled: false, value: "published" },
        regEvents: { enabled: false, value: [] }
      });
      regEventsForm.reset({ regEvents: [] });
      setHasSubmitted(false);
    }
  }, [isOpen, hasSubmitted, regEventsForm]);

  const handleFieldToggle = (field: keyof BulkEditFormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        enabled: !prev[field].enabled
      }
    }));

    // Focus the input field if enabling
    const newEnabled = !formData[field].enabled;
    if (newEnabled) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        const inputId = `${field}Input`;
        const element = document.getElementById(inputId);
        if (element) {
          element.focus();
        }
      }, 0);
    }
  };

  const handleFieldValueChange = (field: keyof BulkEditFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value
      }
    }));
  };

  const handleSubmit = () => {
    const regEvents = regEventsForm.getValues("regEvents") || [];
    onSave({
      ...formData,
      regEvents: {
        ...formData.regEvents,
        value: regEvents
      }
    });
    setHasSubmitted(true);
  };

  // Custom close handler
  const handleClose = () => {
    onClose();
  };

  // Filter out any options with empty IDs to prevent Select.Item errors
  const filteredSignupOptions = signupStatusOptions.filter(opt => isSignupStatus(opt.id));
  const filteredAvailabilityOptions = availabilityStatusOptions.filter(opt => isAvailabilityStatus(opt.id));

  return (
    <BigFormModal
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSubmit}
      title={`Edit ${selectedCount} sessions`}
      saveLabel="Apply changes"
    >
      <div className="space-y-6">
        <p className="text-muted-foreground mb-4">
          Select which fields you want to update across all {selectedCount} selected sessions.
          Only the checked fields will be changed.
        </p>
        
        <div className="space-y-4">
          {/* Session Title Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="sessionTitle" 
                checked={formData.sessionTitle.enabled}
                onCheckedChange={() => handleFieldToggle("sessionTitle")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="sessionTitle" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("sessionTitle");
                }}
              >
                Set session title to:
              </Label>
              <Input
                id="sessionTitleInput"
                value={formData.sessionTitle.value}
                onChange={(e) => handleFieldValueChange("sessionTitle", e.target.value)}
                disabled={!formData.sessionTitle.enabled}
                maxLength={200}
                placeholder="Enter new session title"
                className={formData.sessionTitle.enabled ? "border-[#444]" : ""}
              />
            </div>
          </div>

          {/* Session URL Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="sessionUrl" 
                checked={formData.sessionUrl.enabled}
                onCheckedChange={() => handleFieldToggle("sessionUrl")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="sessionUrl" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("sessionUrl");
                }}
              >
                Session URL
              </Label>
              <Input
                id="sessionUrlInput"
                value={formData.sessionUrl.value}
                onChange={(e) => handleFieldValueChange("sessionUrl", e.target.value)}
                disabled={!formData.sessionUrl.enabled}
                placeholder="https://example.com/info"
                className={formData.sessionUrl.enabled ? "border-[#444]" : ""}
              />
            </div>
          </div>

          {/* Registration URL Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="registrationUrl" 
                checked={formData.registrationUrl.enabled}
                onCheckedChange={() => handleFieldToggle("registrationUrl")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="registrationUrl" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("registrationUrl");
                }}
              >
                Registration URL
              </Label>
              <Input
                id="registrationUrlInput"
                value={formData.registrationUrl.value}
                onChange={(e) => handleFieldValueChange("registrationUrl", e.target.value)}
                disabled={!formData.registrationUrl.enabled}
                placeholder="https://example.com/register"
                className={formData.registrationUrl.enabled ? "border-[#444]" : ""}
              />
            </div>
          </div>

          {/* Registration Status Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="signupStatus" 
                checked={formData.signupStatus.enabled}
                onCheckedChange={() => handleFieldToggle("signupStatus")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="signupStatus" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("signupStatus");
                }}
              >
                Set registration status to:
              </Label>
              <Select 
                value={formData.signupStatus.value}
                onValueChange={(value) => handleFieldValueChange("signupStatus", value)}
                disabled={!formData.signupStatus.enabled}
              >
                <SelectTrigger id="signupStatusInput" className={formData.signupStatus.enabled ? "border-[#444]" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {filteredSignupOptions.length > 0 ? (
                    filteredSignupOptions.map(option => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="su_unknown">Not specified</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Availability Status Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="availabilityStatus" 
                checked={formData.availabilityStatus.enabled}
                onCheckedChange={() => handleFieldToggle("availabilityStatus")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="availabilityStatus" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("availabilityStatus");
                }}
              >
                Set availability status to:
              </Label>
              <Select 
                value={formData.availabilityStatus.value}
                onValueChange={(value) => handleFieldValueChange("availabilityStatus", value)}
                disabled={!formData.availabilityStatus.enabled}
              >
                <SelectTrigger id="availabilityStatusInput" className={formData.availabilityStatus.enabled ? "border-[#444]" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {filteredAvailabilityOptions.length > 0 ? (
                    filteredAvailabilityOptions.map(option => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="av_unknown">Not specified</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Session Description Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="sessionDescription" 
                checked={formData.sessionDescription.enabled}
                onCheckedChange={() => handleFieldToggle("sessionDescription")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="sessionDescription" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("sessionDescription");
                }}
              >
                Set session description to:
              </Label>
              <Textarea
                id="sessionDescriptionInput"
                value={formData.sessionDescription.value}
                onChange={(e) => handleFieldValueChange("sessionDescription", e.target.value)}
                disabled={!formData.sessionDescription.enabled}
                maxLength={2000}
                placeholder="Enter new session description"
                rows={3}
                className={cn(
                  "resize-none",
                  formData.sessionDescription.enabled ? "border-[#444]" : ""
                )}
              />
              <div className="text-xs text-muted-foreground text-right">
                {formData.sessionDescription.value.length}/2000
              </div>
            </div>
          </div>

          {/* Regular Price Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="regularPrice" 
                checked={formData.regularPrice.enabled}
                onCheckedChange={() => handleFieldToggle("regularPrice")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="regularPrice" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("regularPrice");
                }}
              >
                Set regular price to:
              </Label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                </div>
                <Input
                  id="regularPriceInput"
                  type="number"
                  value={formData.regularPrice.value}
                  onChange={(e) => handleFieldValueChange("regularPrice", e.target.value)}
                  disabled={!formData.regularPrice.enabled}
                  placeholder=""
                  min="0"
                  step="1"
                  className={cn(
                    "pl-9",
                    formData.regularPrice.enabled ? "border-[#444]" : ""
                  )}
                />
              </div>
            </div>
          </div>

          {/* Current Sale Price Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="currentSalePrice" 
                checked={formData.currentSalePrice.enabled}
                onCheckedChange={() => handleFieldToggle("currentSalePrice")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="currentSalePrice" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("currentSalePrice");
                }}
              >
                Set current sale price to:
              </Label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                </div>
                <Input
                  id="currentSalePriceInput"
                  type="number"
                  value={formData.currentSalePrice.value}
                  onChange={(e) => handleFieldValueChange("currentSalePrice", e.target.value)}
                  disabled={!formData.currentSalePrice.enabled}
                  placeholder=""
                  min="0"
                  step="1"
                  className={cn(
                    "pl-9",
                    formData.currentSalePrice.enabled ? "border-[#444]" : ""
                  )}
                />
              </div>
            </div>
          </div>

          {/* Price Note Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="priceNote" 
                checked={formData.priceNote.enabled}
                onCheckedChange={() => handleFieldToggle("priceNote")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="priceNote" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("priceNote");
                }}
              >
                Set price note to:
              </Label>
              <Input
                id="priceNoteInput"
                value={formData.priceNote.value}
                onChange={(e) => handleFieldValueChange("priceNote", e.target.value)}
                disabled={!formData.priceNote.enabled}
                maxLength={200}
                placeholder="Enter price note"
                className={formData.priceNote.enabled ? "border-[#444]" : ""}
              />
            </div>
          </div>

          {/* Timing Note Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="timingNote" 
                checked={formData.timingNote.enabled}
                onCheckedChange={() => handleFieldToggle("timingNote")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="timingNote" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("timingNote");
                }}
              >
                Set timing note to:
              </Label>
              <Input
                id="timingNoteInput"
                value={formData.timingNote.value}
                onChange={(e) => handleFieldValueChange("timingNote", e.target.value)}
                disabled={!formData.timingNote.enabled}
                maxLength={200}
                placeholder="Enter timing note"
                className={formData.timingNote.enabled ? "border-[#444]" : ""}
              />
            </div>
          </div>

          {/* Registration dates Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="regEvents" 
                checked={formData.regEvents.enabled}
                onCheckedChange={() => handleFieldToggle("regEvents")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="regEvents" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("regEvents");
                }}
              >
                Set registration dates
              </Label>
              <div className={cn(formData.regEvents.enabled ? "" : "opacity-60")}>
                <Form {...regEventsForm}>
                  <RegistrationEventsSection
                    form={regEventsForm as any}
                    disabled={!formData.regEvents.enabled}
                  />
                </Form>
              </div>
            </div>
          </div>

          {/* Visibility Field */}
          <div className="flex items-start space-x-2">
            <div className="flex items-center pt-3">
              <Checkbox 
                id="visibility" 
                checked={formData.visibility.enabled}
                onCheckedChange={() => handleFieldToggle("visibility")}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label 
                htmlFor="visibility" 
                className="text-sm font-medium cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleFieldToggle("visibility");
                }}
              >
                Set visibility to:
              </Label>
              <Select 
                value={formData.visibility.value}
                onValueChange={(value) => handleFieldValueChange("visibility", value)}
                disabled={!formData.visibility.enabled}
              >
                <SelectTrigger id="visibilityInput" className={formData.visibility.enabled ? "border-[#444]" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </BigFormModal>
  );
};
